import { useEffect, useState } from 'react';
import { renewSpApiCredentials, updateSpApiSellerId } from './authApi';
import { StatusBanner } from '../../components/ui';
import {
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
} from '../ui/classes';
import SpApiSellerIdField from './SpApiSellerIdField';

const initialCredentials = {
  clientId: '',
  clientSecret: '',
  refreshToken: '',
  mode: 'production',
};

function formatSpApiMode(mode) {
  if (mode === 'production') return 'Production';
  if (mode === 'sandbox') return 'Sandbox';
  return 'Unknown';
}

function SpApiSettings({ user, token, onUserUpdate }) {
  const [sellerId, setSellerId] = useState(user.spApiSellerId || '');
  const [sellerStatus, setSellerStatus] = useState({ type: 'idle', message: '' });
  const [isSavingSellerId, setIsSavingSellerId] = useState(false);

  const [form, setForm] = useState(initialCredentials);
  const [renewStatus, setRenewStatus] = useState({ type: 'idle', message: '' });
  const [isRenewing, setIsRenewing] = useState(false);

  useEffect(() => {
    setSellerId(user.spApiSellerId || '');
  }, [user.spApiSellerId]);

  const expiryLabel = user.spApiExpiresAt
    ? new Date(user.spApiExpiresAt).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not set';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSellerIdChange = (event) => {
    setSellerId(event.target.value);
  };

  const handleSaveSellerId = async (event) => {
    event.preventDefault();
    setIsSavingSellerId(true);
    setSellerStatus({ type: 'idle', message: '' });

    try {
      const payload = await updateSpApiSellerId(token, sellerId.trim());
      onUserUpdate(payload.user);
      setSellerStatus({ type: 'success', message: payload.message || 'Seller ID saved.' });
    } catch (error) {
      setSellerStatus({
        type: 'error',
        message: error.message || 'Unable to save Seller ID.',
      });
    } finally {
      setIsSavingSellerId(false);
    }
  };

  const handleRenew = async (event) => {
    event.preventDefault();
    setIsRenewing(true);
    setRenewStatus({ type: 'idle', message: '' });

    try {
      const payload = await renewSpApiCredentials(token, form);
      onUserUpdate(payload.user);
      setForm(initialCredentials);
      setRenewStatus({
        type: 'success',
        message: `SP-API ${formatSpApiMode(payload.mode || payload.user?.spApiMode).toLowerCase()} credentials verified and saved. Stays active while you log in at least once every 30 days.`,
      });
    } catch (error) {
      setRenewStatus({
        type: 'error',
        message: error.message || 'Unable to renew SP-API credentials.',
      });
    } finally {
      setIsRenewing(false);
    }
  };

  return (
    <section className="grid gap-5">
      <div className={`${panelClassName} p-5`}>
        <h2 className="text-base font-semibold">Current SP-API status</h2>
        <p className="crm-text-muted mt-1 text-sm">
          Connection stays active while you log in at least once every 30 days.
          After 30 days of inactivity it expires (Amazon refresh-token policy).
        </p>
        <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1">
            <dt className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
              Connection
            </dt>
            <dd className="font-medium">{user.hasSpApiConfigured ? 'Active' : 'Not configured'}</dd>
          </div>
          <div className="grid gap-1">
            <dt className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
              Environment
            </dt>
            <dd className="font-medium">{formatSpApiMode(user.spApiMode)}</dd>
          </div>
          <div className="grid gap-1">
            <dt className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
              Marketplaces
            </dt>
            <dd className="font-medium">
              {user.spApiMarketplaces?.length ? user.spApiMarketplaces.join(', ') : 'Not set'}
            </dd>
          </div>
          <div className="grid gap-1">
            <dt className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
              Seller ID
            </dt>
            <dd className="font-medium">{user.spApiSellerId || 'Not set'}</dd>
          </div>
          <div className="grid gap-1">
            <dt className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
              Active until
            </dt>
            <dd className="font-medium">{expiryLabel}</dd>
          </div>
        </dl>
      </div>

      <div className={panelClassName}>
        <div className="border-b border-[rgb(var(--crm-border))] px-5 py-4">
          <h2 className="text-base font-semibold">Seller ID (Merchant Token)</h2>
          <p className="crm-text-muted mt-1 text-sm">
            Save your Merchant Token separately — you do not need to re-enter SP-API keys.
          </p>
        </div>

        <form className="grid gap-4 px-5 py-5" onSubmit={handleSaveSellerId} noValidate>
          <SpApiSellerIdField value={sellerId} onChange={handleSellerIdChange} />

          <StatusBanner status={sellerStatus} setStatus={setSellerStatus} />

          <button
            type="submit"
            className={`${secondaryButtonClassName} min-h-11 w-full max-w-xs`}
            disabled={isSavingSellerId || !user.hasSpApiConfigured}
          >
            {isSavingSellerId ? 'Saving...' : 'Save Seller ID'}
          </button>
        </form>
      </div>

      <div className={panelClassName}>
        <div className="border-b border-[rgb(var(--crm-border))] px-5 py-4">
          <h2 className="text-base font-semibold">Renew SP-API credentials</h2>
          <p className="crm-text-muted mt-1 text-sm">
            Only needed when your refresh token expires or you rotate keys in Seller Central.
          </p>
        </div>

        <form className="grid gap-4 px-5 py-5" onSubmit={handleRenew} noValidate>
          <label className="grid gap-1.5">
            <span className={labelClassName}>Environment</span>
            <select className={inputClassName} name="mode" value="production" disabled>
              <option value="production">Production</option>
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className={labelClassName}>Client ID</span>
            <input
              className={inputClassName}
              name="clientId"
              type="text"
              autoComplete="off"
              value={form.clientId}
              onChange={handleChange}
              required
            />
          </label>

          <label className="grid gap-1.5">
            <span className={labelClassName}>Client Secret</span>
            <input
              className={inputClassName}
              name="clientSecret"
              type="password"
              autoComplete="off"
              value={form.clientSecret}
              onChange={handleChange}
              required
            />
          </label>

          <label className="grid gap-1.5">
            <span className={labelClassName}>Refresh Token</span>
            <input
              className={inputClassName}
              name="refreshToken"
              type="password"
              autoComplete="off"
              value={form.refreshToken}
              onChange={handleChange}
              required
            />
          </label>

          <StatusBanner status={renewStatus} setStatus={setRenewStatus} />

          <button
            type="submit"
            className={`${primaryButtonClassName} min-h-11 w-full max-w-xs`}
            disabled={isRenewing}
          >
            {isRenewing ? 'Verifying...' : 'Verify & renew credentials'}
          </button>
        </form>
      </div>
    </section>
  );
}

export default SpApiSettings;
